import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AdminProjectTemplate,
  ForgejoRepoInfo,
  TemplateIconColor,
  TemplateIconStyle,
  TemplateTagColorToken,
  TemplateTagDefinition,
  TemplateTagGroup,
} from '@claude-code-api/shared';
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronsUpDown,
  GripVertical,
  ImagePlus,
  Loader2,
  Plus,
  Settings2,
  Star,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  TEMPLATE_ICON_OPTIONS,
  getCompactTemplateBadgeClassName,
  getTemplateIconClassName,
  getTemplateIconOption,
  getTemplateIconStyleAttribute,
  getTemplateTagColorClassName,
} from '@/lib/template-catalog';

type AdminTab = 'templates' | 'groups' | 'tags';
type ModalType = null | 'template' | 'group' | 'tag';
type TagGroupRow = TemplateTagGroup & {
  tagDefinitions?: TemplateTagDefinition[];
};

type TemplateCatalogResponse = { data?: AdminProjectTemplate[] };
type TagGroupResponse = { data?: TagGroupRow[] };
type TagDefinitionResponse = { data?: TemplateTagDefinition[] };

const colorOptions: TemplateTagColorToken[] = [
  'slate',
  'blue',
  'teal',
  'emerald',
  'amber',
  'rose',
  'violet',
  'cyan',
];
const iconPageSize = 60;

const defaultPlainIconColor = '#0f766e';
const defaultGradientStart = '#0f766e';
const defaultGradientEnd = '#06b6d4';
const tokenColorMap: Record<string, string> = {
  slate: '#475569',
  blue: '#2563eb',
  teal: '#0f766e',
  emerald: '#059669',
  amber: '#f59e0b',
  rose: '#f43f5e',
  violet: '#7c3aed',
  cyan: '#0891b2',
};

function normalizeIconColorInput(value: string): string {
  const resolved = tokenColorMap[value] || value || defaultPlainIconColor;
  return /^#[0-9a-fA-F]{6}$/.test(resolved) ? resolved : defaultPlainIconColor;
}

function getGradientStops(value: string): [string, string] {
  const matches = String(value || '').match(/#[0-9a-fA-F]{6}/g);
  if (matches?.length) return [matches[0], matches[1] || defaultGradientEnd];
  return [defaultGradientStart, defaultGradientEnd];
}

function createGradient(start: string, end: string) {
  return `linear-gradient(135deg, ${start}, ${end})`;
}

const defaultTemplateForm = {
  id: '',
  name: '',
  description: '',
  detailContent: '',
  icon: 'briefcase',
  iconStyle: 'gradient' as TemplateIconStyle,
  iconColor: 'teal' as TemplateIconColor,
  coverImageUrl: '',
  screenshotUrlsText: '',
  forgejoRepoFullName: '',
  enabled: true,
  featured: false,
  sortOrder: 0,
  tagIds: [] as string[],
};

const defaultGroupForm = {
  id: '',
  name: '',
  description: '',
  sortOrder: 0,
  enabled: true,
};

const defaultTagForm = {
  id: '',
  name: '',
  description: '',
  groupId: '',
  colorToken: 'slate' as TemplateTagColorToken,
  sortOrder: 0,
  enabled: true,
};

function normalizeTemplateFormForCompare(form: typeof defaultTemplateForm) {
  return JSON.stringify({
    ...form,
    name: form.name.trim(),
    description: form.description.trim(),
    detailContent: form.detailContent.trim(),
    coverImageUrl: form.coverImageUrl.trim(),
    forgejoRepoFullName: form.forgejoRepoFullName.trim(),
    screenshotUrlsText: form.screenshotUrlsText
      .split('\n')
      .map((value) => value.trim())
      .filter(Boolean)
      .join('\n'),
    tagIds: [...form.tagIds],
  });
}

export default function AdminTemplateCatalogPage() {
  const { t } = useTranslation(['adminTemplates', 'common']);
  const [activeTab, setActiveTab] = useState<AdminTab>('templates');
  const [templates, setTemplates] = useState<AdminProjectTemplate[]>([]);
  const [tagGroups, setTagGroups] = useState<TagGroupRow[]>([]);
  const [tagDefinitions, setTagDefinitions] = useState<TemplateTagDefinition[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [templateForm, setTemplateForm] = useState(defaultTemplateForm);
  const [templateFormInitialSnapshot, setTemplateFormInitialSnapshot] =
    useState(() => normalizeTemplateFormForCompare(defaultTemplateForm));
  const [showTemplateCloseConfirm, setShowTemplateCloseConfirm] =
    useState(false);
  const [groupForm, setGroupForm] = useState(defaultGroupForm);
  const [tagForm, setTagForm] = useState(defaultTagForm);
  const [templateTagQuery, setTemplateTagQuery] = useState('');
  const [templateTagMenuOpen, setTemplateTagMenuOpen] = useState(false);
  const [templateRepoQuery, setTemplateRepoQuery] = useState('');
  const [templateRepoMenuOpen, setTemplateRepoMenuOpen] = useState(false);
  const [visualDialogOpen, setVisualDialogOpen] = useState(false);
  const [screenshotDialogOpen, setScreenshotDialogOpen] = useState(false);
  const [visualIconQuery, setVisualIconQuery] = useState('');
  const [visualIconPage, setVisualIconPage] = useState(1);
  const [templateRepos, setTemplateRepos] = useState<ForgejoRepoInfo[]>([]);
  const [loadingTemplateRepos, setLoadingTemplateRepos] = useState(false);
  const [uploadingScreenshots, setUploadingScreenshots] = useState(false);
  const [templateTagMenuRect, setTemplateTagMenuRect] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);
  const [templateRepoMenuRect, setTemplateRepoMenuRect] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);
  const templateTagAnchorRef = useRef<HTMLDivElement | null>(null);
  const templateRepoAnchorRef = useRef<HTMLDivElement | null>(null);
  const screenshotInputRef = useRef<HTMLInputElement | null>(null);
  const visualIconScrollRef = useRef<HTMLDivElement | null>(null);
  const templateDialogScrollRef = useRef<HTMLDivElement | null>(null);
  const templateDialogContentRef = useRef<HTMLDivElement | null>(null);

  const orderedGroups = useMemo(
    () =>
      [...tagGroups].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      ),
    [tagGroups],
  );
  const orderedTags = useMemo(
    () =>
      [...tagDefinitions].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      ),
    [tagDefinitions],
  );
  const managedTagIds = useMemo(
    () => new Set(orderedTags.map((tag) => tag.id)),
    [orderedTags],
  );
  const orderedTemplates = useMemo(
    () =>
      [...templates].sort(
        (a, b) =>
          a.sortOrder - b.sortOrder ||
          Number(b.featured) - Number(a.featured) ||
          a.name.localeCompare(b.name),
      ),
    [templates],
  );

  const templatePreviewIconOption = getTemplateIconOption(templateForm.icon);
  const TemplatePreviewIcon = templatePreviewIconOption.icon;
  const selectedTemplateAppearance = `${templateForm.iconStyle === 'gradient' ? t('adminTemplates:visualDialog.tabs.gradient') : t('adminTemplates:visualDialog.tabs.color')}`;
  const plainIconColor =
    templateForm.iconStyle === 'plain'
      ? normalizeIconColorInput(templateForm.iconColor)
      : defaultPlainIconColor;
  const [gradientStart, gradientEnd] = getGradientStops(templateForm.iconColor);
  const filteredTemplateIconOptions = useMemo(() => {
    const query = visualIconQuery.trim().toLowerCase();
    if (!query) return TEMPLATE_ICON_OPTIONS;
    return TEMPLATE_ICON_OPTIONS.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.value.includes(query),
    );
  }, [visualIconQuery]);
  const visibleTemplateIconOptions = useMemo(
    () => filteredTemplateIconOptions.slice(0, visualIconPage * iconPageSize),
    [filteredTemplateIconOptions, visualIconPage],
  );
  const hasMoreTemplateIcons =
    visibleTemplateIconOptions.length < filteredTemplateIconOptions.length;

  const loadMoreTemplateIcons = useCallback(() => {
    setVisualIconPage((current) =>
      current * iconPageSize >= filteredTemplateIconOptions.length
        ? current
        : current + 1,
    );
  }, [filteredTemplateIconOptions.length]);

  const handleTemplateIconScroll = useCallback(() => {
    const element = visualIconScrollRef.current;
    if (!element || !hasMoreTemplateIcons) return;
    if (
      element.scrollTop + element.clientHeight >=
      element.scrollHeight - 160
    ) {
      loadMoreTemplateIcons();
    }
  }, [hasMoreTemplateIcons, loadMoreTemplateIcons]);

  const syncFloatingMenus = useCallback(() => {
    const contentRect =
      templateDialogContentRef.current?.getBoundingClientRect();
    if (templateTagAnchorRef.current && contentRect) {
      const rect = templateTagAnchorRef.current.getBoundingClientRect();
      setTemplateTagMenuRect({
        left: rect.left - contentRect.left,
        top: rect.bottom - contentRect.top + 8,
        width: rect.width,
      });
    }
    if (templateRepoAnchorRef.current && contentRect) {
      const rect = templateRepoAnchorRef.current.getBoundingClientRect();
      setTemplateRepoMenuRect({
        left: rect.left - contentRect.left,
        top: rect.bottom - contentRect.top + 8,
        width: rect.width,
      });
    }
  }, []);

  const selectedTemplateRepo = useMemo(
    () =>
      templateRepos.find(
        (repo) => repo.fullName === templateForm.forgejoRepoFullName,
      ) || null,
    [templateForm.forgejoRepoFullName, templateRepos],
  );

  const filteredTemplateRepos = useMemo(() => {
    const query = templateRepoQuery.trim().toLowerCase();
    return templateRepos.filter(
      (repo) =>
        !query ||
        repo.name.toLowerCase().includes(query) ||
        repo.fullName.toLowerCase().includes(query) ||
        String(repo.description || '')
          .toLowerCase()
          .includes(query),
    );
  }, [templateRepoQuery, templateRepos]);

  const selectedTemplateTags = useMemo(
    () => orderedTags.filter((tag) => templateForm.tagIds.includes(tag.id)),
    [orderedTags, templateForm.tagIds],
  );
  const templateScreenshotUrls = useMemo(
    () =>
      templateForm.screenshotUrlsText
        .split('\n')
        .map((value) => value.trim())
        .filter(Boolean),
    [templateForm.screenshotUrlsText],
  );

  const deriveTemplateId = useCallback(() => {
    const preferred =
      editingId ||
      templateForm.id ||
      templateForm.forgejoRepoFullName.split('/').pop() ||
      templateForm.name;
    const normalized = preferred
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return normalized || 'draft-template';
  }, [
    editingId,
    templateForm.forgejoRepoFullName,
    templateForm.id,
    templateForm.name,
  ]);

  const templateAssetFolder = useMemo(
    () => `data/template-showcase/${deriveTemplateId()}`,
    [deriveTemplateId],
  );
  const templateFormIsDirty = useMemo(
    () =>
      normalizeTemplateFormForCompare(templateForm) !==
      templateFormInitialSnapshot,
    [templateForm, templateFormInitialSnapshot],
  );

  const filteredTemplateTagGroups = useMemo(() => {
    const query = templateTagQuery.trim().toLowerCase();
    return orderedGroups
      .map((group) => ({
        group,
        tags: orderedTags.filter((tag) => {
          if (tag.groupId !== group.id) return false;
          if (!query) return true;
          return [tag.name, tag.id, tag.description || '', group.name]
            .join(' ')
            .toLowerCase()
            .includes(query);
        }),
      }))
      .filter((entry) => entry.tags.length > 0);
  }, [orderedGroups, orderedTags, templateTagQuery]);

  const addTemplateTag = (tagId: string) => {
    setTemplateForm((current) =>
      current.tagIds.includes(tagId)
        ? current
        : { ...current, tagIds: [...current.tagIds, tagId] },
    );
    setTemplateTagQuery('');
    setTemplateTagMenuOpen(false);
    setTemplateRepoQuery('');
    setTemplateRepoMenuOpen(false);
  };

  const removeTemplateTag = (tagId: string) => {
    setTemplateForm((current) => ({
      ...current,
      tagIds: current.tagIds.filter((value) => value !== tagId),
    }));
  };

  const syncScreenshotUrls = (
    urls: string[],
    preferredCover?: string | null,
  ) => {
    const deduped = Array.from(
      new Set(urls.map((value) => value.trim()).filter(Boolean)),
    );
    setTemplateForm((current) => ({
      ...current,
      screenshotUrlsText: deduped.join('\n'),
      coverImageUrl:
        preferredCover && deduped.includes(preferredCover)
          ? preferredCover
          : current.coverImageUrl && deduped.includes(current.coverImageUrl)
            ? current.coverImageUrl
            : deduped[0] || '',
    }));
  };

  const moveScreenshot = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= templateScreenshotUrls.length) return;
    const next = [...templateScreenshotUrls];
    const [item] = next.splice(index, 1);
    next.splice(nextIndex, 0, item);
    syncScreenshotUrls(next, templateForm.coverImageUrl);
  };

  const removeScreenshotUrl = (url: string) => {
    syncScreenshotUrls(
      templateScreenshotUrls.filter((value) => value !== url),
      templateForm.coverImageUrl === url ? null : templateForm.coverImageUrl,
    );
  };

  const uploadScreenshots = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const templateId = deriveTemplateId();
    setTemplateForm((current) =>
      current.id ? current : { ...current, id: templateId },
    );
    const formData = new FormData();
    formData.append('templateId', templateId);
    Array.from(files).forEach((file) => {
      formData.append('files', file);
    });

    setUploadingScreenshots(true);
    try {
      const res = await fetch(
        '/api/admin/template-catalog/upload-screenshots',
        {
          method: 'POST',
          credentials: 'include',
          body: formData,
        },
      );
      const json = (await res.json()) as {
        error?: string;
        data?: Array<{ url: string }>;
      };
      if (!res.ok) throw new Error(json.error || 'upload_failed');

      const uploadedUrls = (json.data || [])
        .map((item) => item.url)
        .filter(Boolean);
      setTemplateForm((current) => {
        const merged = Array.from(
          new Set([
            ...current.screenshotUrlsText
              .split('\n')
              .map((value) => value.trim())
              .filter(Boolean),
            ...uploadedUrls,
          ]),
        );
        return {
          ...current,
          screenshotUrlsText: merged.join('\n'),
          coverImageUrl:
            current.coverImageUrl || uploadedUrls[0] || current.coverImageUrl,
        };
      });
      toast.success(
        t('adminTemplates:messages.uploadedScreenshots', {
          count: uploadedUrls.length,
        }),
      );
    } catch (error) {
      console.error('Failed to upload screenshots:', error);
      toast.error(t('adminTemplates:errors.uploadScreenshots'));
    } finally {
      setUploadingScreenshots(false);
      if (screenshotInputRef.current) screenshotInputRef.current.value = '';
    }
  };

  const fetchTemplateRepos = async () => {
    setLoadingTemplateRepos(true);
    try {
      const res = await fetch('/api/admin/forgejo/template-repos', {
        credentials: 'include',
      });
      const json = (await res.json()) as { data?: ForgejoRepoInfo[] };
      if (!res.ok) throw new Error('load_failed');
      setTemplateRepos(json.data || []);
    } catch (error) {
      console.error('Failed to load template repos:', error);
      toast.error(t('adminTemplates:errors.loadTemplateRepos'));
    } finally {
      setLoadingTemplateRepos(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [templatesRes, groupsRes, tagsRes] = await Promise.all([
        fetch('/api/admin/template-catalog', { credentials: 'include' }),
        fetch('/api/admin/template-tag-groups', { credentials: 'include' }),
        fetch('/api/admin/template-tags', { credentials: 'include' }),
      ]);

      const [templatesJson, groupsJson, tagsJson] = await Promise.all([
        templatesRes.json() as Promise<TemplateCatalogResponse>,
        groupsRes.json() as Promise<TagGroupResponse>,
        tagsRes.json() as Promise<TagDefinitionResponse>,
      ]);

      if (!templatesRes.ok || !groupsRes.ok || !tagsRes.ok) {
        throw new Error('load_failed');
      }

      setTemplates(templatesJson.data || []);
      setTagGroups(groupsJson.data || []);
      setTagDefinitions(tagsJson.data || []);
    } catch (error) {
      console.error('Failed to load template admin data:', error);
      toast.error(t('adminTemplates:errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchAll();
    void fetchTemplateRepos();
  }, []);

  useEffect(() => {
    setVisualIconPage(1);
    visualIconScrollRef.current?.scrollTo({ top: 0 });
  }, [visualIconQuery, visualDialogOpen]);

  useEffect(() => {
    if (!templateTagMenuOpen && !templateRepoMenuOpen) return;
    syncFloatingMenus();
    const handleWindowChange = () => syncFloatingMenus();
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);
    return () => {
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [syncFloatingMenus, templateRepoMenuOpen, templateTagMenuOpen]);

  useEffect(() => {
    if (!templateTagMenuOpen && !templateRepoMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-template-floating-menu]')) return;
      if (templateTagAnchorRef.current?.contains(target)) return;
      if (templateRepoAnchorRef.current?.contains(target)) return;

      setTemplateTagMenuOpen(false);
      setTemplateRepoMenuOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [templateRepoMenuOpen, templateTagMenuOpen]);

  const resetModal = () => {
    setModalType(null);
    setEditingId(null);
    setTemplateForm(defaultTemplateForm);
    setTemplateFormInitialSnapshot(
      normalizeTemplateFormForCompare(defaultTemplateForm),
    );
    setGroupForm(defaultGroupForm);
    setTagForm(defaultTagForm);
    setTemplateTagQuery('');
    setTemplateTagMenuOpen(false);
    setTemplateRepoQuery('');
    setTemplateRepoMenuOpen(false);
    setVisualDialogOpen(false);
    setScreenshotDialogOpen(false);
    setVisualIconQuery('');
    setVisualIconPage(1);
    setShowTemplateCloseConfirm(false);
  };

  const openTemplateCreate = () => {
    setEditingId(null);
    const nextForm = { ...defaultTemplateForm };
    setTemplateForm(nextForm);
    setTemplateFormInitialSnapshot(normalizeTemplateFormForCompare(nextForm));
    setTemplateRepoQuery('');
    setTemplateRepoMenuOpen(false);
    setVisualDialogOpen(false);
    setScreenshotDialogOpen(false);
    setVisualIconQuery('');
    setVisualIconPage(1);
    setModalType('template');
  };

  const openTemplateEdit = (template: AdminProjectTemplate) => {
    const nextForm = {
      id: template.id,
      name: template.name,
      description: template.description,
      detailContent: template.detailContent || '',
      icon: template.icon || 'briefcase',
      iconStyle: template.iconStyle || 'gradient',
      iconColor: template.iconColor || 'teal',
      coverImageUrl: template.coverImageUrl || '',
      screenshotUrlsText: (template.screenshotUrls || []).join('\n'),
      forgejoRepoFullName: template.forgejoRepoFullName,
      enabled: template.enabled,
      featured: template.featured,
      sortOrder: template.sortOrder,
      tagIds: (template.templateTagIds || []).filter((tagId) =>
        managedTagIds.has(tagId),
      ),
    };
    setEditingId(template.id);
    setTemplateTagQuery('');
    setTemplateTagMenuOpen(false);
    setTemplateRepoQuery('');
    setTemplateRepoMenuOpen(false);
    setVisualIconQuery('');
    setVisualIconPage(1);
    setTemplateForm(nextForm);
    setTemplateFormInitialSnapshot(normalizeTemplateFormForCompare(nextForm));
    setVisualDialogOpen(false);
    setScreenshotDialogOpen(false);
    setModalType('template');
  };

  const requestTemplateDialogClose = () => {
    if (saving) return;
    if (!templateFormIsDirty) {
      resetModal();
      return;
    }
    setShowTemplateCloseConfirm(true);
  };

  const openGroupCreate = () => {
    setEditingId(null);
    setGroupForm(defaultGroupForm);
    setModalType('group');
  };

  const openGroupEdit = (group: TagGroupRow) => {
    setEditingId(group.id);
    setGroupForm({
      id: group.id,
      name: group.name,
      description: group.description || '',
      sortOrder: group.sortOrder,
      enabled: group.enabled,
    });
    setModalType('group');
  };

  const openTagCreate = () => {
    setEditingId(null);
    setTagForm({ ...defaultTagForm, groupId: orderedGroups[0]?.id || '' });
    setModalType('tag');
  };

  const openTagEdit = (tag: TemplateTagDefinition) => {
    setEditingId(tag.id);
    setTagForm({
      id: tag.id,
      name: tag.name,
      description: tag.description || '',
      groupId: tag.groupId,
      colorToken: tag.colorToken,
      sortOrder: tag.sortOrder,
      enabled: tag.enabled,
    });
    setModalType('tag');
  };

  const saveTemplate = async (): Promise<boolean> => {
    setSaving(true);
    try {
      const derivedId = deriveTemplateId();
      const payload = {
        id: derivedId,
        name: templateForm.name,
        description: templateForm.description,
        detailContent: templateForm.detailContent,
        icon: templateForm.icon,
        iconStyle: templateForm.iconStyle,
        iconColor: templateForm.iconColor,
        coverImageUrl: templateForm.coverImageUrl.trim() || null,
        screenshotUrls: templateForm.screenshotUrlsText
          .split('\n')
          .map((value) => value.trim())
          .filter(Boolean),
        forgejoRepoFullName: templateForm.forgejoRepoFullName,
        enabled: templateForm.enabled,
        featured: templateForm.featured,
        sortOrder: templateForm.sortOrder,
        tagIds: templateForm.tagIds,
      };
      const res = await fetch(
        editingId
          ? `/api/admin/template-catalog/${editingId}`
          : '/api/admin/template-catalog',
        {
          method: editingId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'save_failed');
      toast.success(
        editingId
          ? t('adminTemplates:messages.updated')
          : t('adminTemplates:messages.created'),
      );
      resetModal();
      await fetchAll();
      return true;
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error(t('adminTemplates:errors.save'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveGroup = async () => {
    setSaving(true);
    try {
      const payload = {
        id: groupForm.id,
        name: groupForm.name,
        description: groupForm.description || null,
        sortOrder: groupForm.sortOrder,
        enabled: groupForm.enabled,
      };
      const res = await fetch(
        editingId
          ? `/api/admin/template-tag-groups/${editingId}`
          : '/api/admin/template-tag-groups',
        {
          method: editingId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'save_failed');
      toast.success(
        editingId
          ? t('adminTemplates:messages.groupUpdated')
          : t('adminTemplates:messages.groupCreated'),
      );
      resetModal();
      await fetchAll();
    } catch (error) {
      console.error('Failed to save tag group:', error);
      toast.error(t('adminTemplates:errors.saveGroup'));
    } finally {
      setSaving(false);
    }
  };

  const saveTag = async () => {
    setSaving(true);
    try {
      const payload = {
        id: tagForm.id,
        name: tagForm.name,
        description: tagForm.description || null,
        groupId: tagForm.groupId,
        colorToken: tagForm.colorToken,
        sortOrder: tagForm.sortOrder,
        enabled: tagForm.enabled,
      };
      const res = await fetch(
        editingId
          ? `/api/admin/template-tags/${editingId}`
          : '/api/admin/template-tags',
        {
          method: editingId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'save_failed');
      toast.success(
        editingId
          ? t('adminTemplates:messages.tagUpdated')
          : t('adminTemplates:messages.tagCreated'),
      );
      resetModal();
      await fetchAll();
    } catch (error) {
      console.error('Failed to save tag:', error);
      toast.error(t('adminTemplates:errors.saveTag'));
    } finally {
      setSaving(false);
    }
  };

  const toggleTemplate = async (template: AdminProjectTemplate) => {
    const res = await fetch(
      `/api/admin/template-catalog/${template.id}/toggle-enabled`,
      { method: 'POST', credentials: 'include' },
    );
    const data = await res.json();
    if (!res.ok) {
      toast.error(data.error || t('adminTemplates:errors.toggle'));
      return;
    }
    toast.success(
      template.enabled
        ? t('adminTemplates:messages.disabled')
        : t('adminTemplates:messages.enabled'),
    );
    await fetchAll();
  };

  const toggleGroup = async (group: TagGroupRow) => {
    const res = await fetch(
      `/api/admin/template-tag-groups/${group.id}/toggle-enabled`,
      { method: 'POST', credentials: 'include' },
    );
    const data = await res.json();
    if (!res.ok) {
      toast.error(data.error || t('adminTemplates:errors.toggleGroup'));
      return;
    }
    toast.success(
      group.enabled
        ? t('adminTemplates:messages.groupDisabled')
        : t('adminTemplates:messages.groupEnabled'),
    );
    await fetchAll();
  };

  const toggleTag = async (tag: TemplateTagDefinition) => {
    const res = await fetch(
      `/api/admin/template-tags/${tag.id}/toggle-enabled`,
      { method: 'POST', credentials: 'include' },
    );
    const data = await res.json();
    if (!res.ok) {
      toast.error(data.error || t('adminTemplates:errors.toggleTag'));
      return;
    }
    toast.success(
      tag.enabled
        ? t('adminTemplates:messages.tagDisabled')
        : t('adminTemplates:messages.tagEnabled'),
    );
    await fetchAll();
  };

  const moveTemplate = async (id: string, direction: -1 | 1) => {
    const index = orderedTemplates.findIndex((item) => item.id === id);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= orderedTemplates.length)
      return;
    const next = [...orderedTemplates];
    const [item] = next.splice(index, 1);
    next.splice(nextIndex, 0, item);
    const res = await fetch('/api/admin/template-catalog/reorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        items: next.map((row, idx) => ({
          id: row.id,
          sortOrder: (idx + 1) * 10,
        })),
      }),
    });
    if (!res.ok) toast.error(t('adminTemplates:errors.reorder'));
    else await fetchAll();
  };

  const moveGroup = async (id: string, direction: -1 | 1) => {
    const index = orderedGroups.findIndex((item) => item.id === id);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= orderedGroups.length) return;
    const next = [...orderedGroups];
    const [item] = next.splice(index, 1);
    next.splice(nextIndex, 0, item);
    const res = await fetch('/api/admin/template-tag-groups/reorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        items: next.map((row, idx) => ({
          id: row.id,
          sortOrder: (idx + 1) * 10,
        })),
      }),
    });
    if (!res.ok) toast.error(t('adminTemplates:errors.reorderGroup'));
    else await fetchAll();
  };

  const moveTag = async (id: string, direction: -1 | 1) => {
    const index = orderedTags.findIndex((item) => item.id === id);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= orderedTags.length) return;
    const next = [...orderedTags];
    const [item] = next.splice(index, 1);
    next.splice(nextIndex, 0, item);
    const res = await fetch('/api/admin/template-tags/reorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        items: next.map((row, idx) => ({
          id: row.id,
          sortOrder: (idx + 1) * 10,
          groupId: row.groupId,
        })),
      }),
    });
    if (!res.ok) toast.error(t('adminTemplates:errors.reorderTag'));
    else await fetchAll();
  };

  const deleteGroup = async (group: TagGroupRow) => {
    if (
      !window.confirm(
        t('adminTemplates:confirm.deleteGroup', { name: group.name }),
      )
    )
      return;
    const res = await fetch(`/api/admin/template-tag-groups/${group.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok)
      toast.error(
        (data as { error?: string }).error ||
          t('adminTemplates:errors.deleteGroup'),
      );
    else {
      toast.success(t('adminTemplates:messages.groupDeleted'));
      await fetchAll();
    }
  };

  const deleteTag = async (tag: TemplateTagDefinition) => {
    if (
      !window.confirm(t('adminTemplates:confirm.deleteTag', { name: tag.name }))
    )
      return;
    const res = await fetch(`/api/admin/template-tags/${tag.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok)
      toast.error(
        (data as { error?: string }).error ||
          t('adminTemplates:errors.deleteTag'),
      );
    else {
      toast.success(t('adminTemplates:messages.tagDeleted'));
      await fetchAll();
    }
  };

  const renderTemplateTagPicker = () => (
    <div className="space-y-3 rounded-xl border p-4">
      <div>
        <h3 className="text-sm font-medium">
          {t('adminTemplates:form.templateTags')}
        </h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {selectedTemplateTags.map((tag) => (
          <Badge
            key={tag.id}
            variant="outline"
            className={getCompactTemplateBadgeClassName(
              `${getTemplateTagColorClassName(tag.colorToken)} pr-1`,
            )}
          >
            {tag.name}
            <button
              type="button"
              className="ml-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm text-current/70 transition hover:text-current"
              onClick={() => removeTemplateTag(tag.id)}
              aria-label={t('adminTemplates:actions.removeTag', {
                name: tag.name,
              })}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="relative">
        <div
          ref={templateTagAnchorRef}
          className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2"
        >
          <Input
            value={templateTagQuery}
            onChange={(e) => {
              setTemplateTagQuery(e.target.value);
              setTemplateTagMenuOpen(true);
              syncFloatingMenus();
            }}
            onFocus={() => {
              setTemplateTagMenuOpen(true);
              syncFloatingMenus();
            }}
            placeholder={t('adminTemplates:form.templateTagsPlaceholder')}
            className="border-0 px-0 shadow-none focus-visible:ring-0"
          />
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center text-muted-foreground"
            onClick={() => {
              setTemplateTagMenuOpen((current) => !current);
              syncFloatingMenus();
            }}
            aria-label={t('adminTemplates:actions.browseTags')}
          >
            <ChevronsUpDown className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('adminTemplates:title')}
          description={t('adminTemplates:description')}
        />
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('adminTemplates:title')}
        description={t('adminTemplates:description')}
      />

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as AdminTab)}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="templates">
            {t('adminTemplates:tabs.templates')}
          </TabsTrigger>
          <TabsTrigger value="groups">
            {t('adminTemplates:tabs.groups')}
          </TabsTrigger>
          <TabsTrigger value="tags">
            {t('adminTemplates:tabs.tags')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openTemplateCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              {t('adminTemplates:actions.create')}
            </Button>
          </div>
          <div className="rounded-2xl border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminTemplates:table.name')}</TableHead>
                  <TableHead>{t('adminTemplates:table.repo')}</TableHead>
                  <TableHead>{t('adminTemplates:table.tags')}</TableHead>
                  <TableHead>{t('adminTemplates:table.status')}</TableHead>
                  <TableHead>{t('adminTemplates:table.order')}</TableHead>
                  <TableHead>{t('adminTemplates:table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderedTemplates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{template.name}</span>
                          {template.featured ? (
                            <Badge
                              variant="outline"
                              className={getCompactTemplateBadgeClassName(
                                'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
                              )}
                            >
                              <Star className="mr-1 h-3 w-3 fill-current" />
                              {t('adminTemplates:status.featured')}
                            </Badge>
                          ) : null}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {template.id}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {template.forgejoRepoFullName}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const tagDefinitions = (
                          template.tagDefinitions || []
                        ).filter((tag) => managedTagIds.has(tag.id));
                        const visibleTags = tagDefinitions.slice(0, 3);
                        const hiddenTagCount = Math.max(
                          0,
                          tagDefinitions.length - visibleTags.length,
                        );
                        return (
                          <div className="flex flex-wrap gap-1">
                            {visibleTags.map((tag) => (
                              <Badge
                                key={`${template.id}-${tag.id}`}
                                variant="outline"
                                className={getCompactTemplateBadgeClassName(
                                  getTemplateTagColorClassName(tag.colorToken),
                                )}
                              >
                                {tag.name}
                              </Badge>
                            ))}
                            {hiddenTagCount > 0 ? (
                              <div className="group/admin-template-tags relative">
                                <Badge
                                  variant="outline"
                                  className={getCompactTemplateBadgeClassName(
                                    'cursor-default border-dashed bg-muted/60 text-muted-foreground',
                                  )}
                                >
                                  +{hiddenTagCount}
                                </Badge>
                                <div className="pointer-events-none absolute bottom-full left-0 z-20 mb-2 w-72 rounded-xl border bg-popover p-3 text-popover-foreground opacity-0 shadow-lg transition-opacity duration-150 group-hover/admin-template-tags:opacity-100">
                                  <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    {t('adminTemplates:table.allTags')}
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {tagDefinitions.map((tag) => (
                                      <Badge
                                        key={`${template.id}-popover-${tag.id}`}
                                        variant="outline"
                                        className={getCompactTemplateBadgeClassName(
                                          getTemplateTagColorClassName(
                                            tag.colorToken,
                                          ),
                                        )}
                                      >
                                        {tag.name}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <Badge
                          variant={template.enabled ? 'default' : 'secondary'}
                          className={getCompactTemplateBadgeClassName()}
                        >
                          {template.enabled
                            ? t('adminTemplates:status.enabled')
                            : t('adminTemplates:status.disabled')}
                        </Badge>
                        <div className="text-xs text-muted-foreground">
                          {t('adminTemplates:table.usageCount', {
                            count: template.usageCount || 0,
                          })}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{template.sortOrder}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => void moveTemplate(template.id, -1)}
                          aria-label={t('adminTemplates:actions.moveUp')}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => void moveTemplate(template.id, 1)}
                          aria-label={t('adminTemplates:actions.moveDown')}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openTemplateEdit(template)}
                        >
                          {t('adminTemplates:actions.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void toggleTemplate(template)}
                        >
                          {template.enabled
                            ? t('adminTemplates:actions.disable')
                            : t('adminTemplates:actions.enable')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="groups" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openGroupCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              {t('adminTemplates:actions.createGroup')}
            </Button>
          </div>
          <div className="rounded-2xl border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminTemplates:groupTable.name')}</TableHead>
                  <TableHead>{t('adminTemplates:groupTable.tags')}</TableHead>
                  <TableHead>{t('adminTemplates:groupTable.status')}</TableHead>
                  <TableHead>{t('adminTemplates:groupTable.order')}</TableHead>
                  <TableHead>
                    {t('adminTemplates:groupTable.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderedGroups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <span className="font-medium">{group.name}</span>
                        <p className="text-xs text-muted-foreground">
                          {group.id}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{group.tagDefinitions?.length || 0}</TableCell>
                    <TableCell>
                      <Badge
                        variant={group.enabled ? 'default' : 'secondary'}
                        className={getCompactTemplateBadgeClassName()}
                      >
                        {group.enabled
                          ? t('adminTemplates:status.enabled')
                          : t('adminTemplates:status.disabled')}
                      </Badge>
                    </TableCell>
                    <TableCell>{group.sortOrder}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => void moveGroup(group.id, -1)}
                          aria-label={t('adminTemplates:actions.moveUp')}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => void moveGroup(group.id, 1)}
                          aria-label={t('adminTemplates:actions.moveDown')}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openGroupEdit(group)}
                        >
                          {t('adminTemplates:actions.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void toggleGroup(group)}
                        >
                          {group.enabled
                            ? t('adminTemplates:actions.disable')
                            : t('adminTemplates:actions.enable')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => void deleteGroup(group)}
                          aria-label={t('adminTemplates:actions.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="tags" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openTagCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              {t('adminTemplates:actions.createTag')}
            </Button>
          </div>
          <div className="rounded-2xl border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminTemplates:tagTable.name')}</TableHead>
                  <TableHead>{t('adminTemplates:tagTable.group')}</TableHead>
                  <TableHead>{t('adminTemplates:tagTable.color')}</TableHead>
                  <TableHead>
                    {t('adminTemplates:tagTable.sortOrder')}
                  </TableHead>
                  <TableHead>{t('adminTemplates:tagTable.status')}</TableHead>
                  <TableHead>{t('adminTemplates:tagTable.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderedTags.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <span className="font-medium">{tag.name}</span>
                        <p className="text-xs text-muted-foreground">
                          {tag.id}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{tag.group?.name || tag.groupId}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={getCompactTemplateBadgeClassName(
                          getTemplateTagColorClassName(tag.colorToken),
                        )}
                      >
                        {tag.colorToken}
                      </Badge>
                    </TableCell>
                    <TableCell>{tag.sortOrder}</TableCell>
                    <TableCell>
                      <Badge
                        variant={tag.enabled ? 'default' : 'secondary'}
                        className={getCompactTemplateBadgeClassName()}
                      >
                        {tag.enabled
                          ? t('adminTemplates:status.enabled')
                          : t('adminTemplates:status.disabled')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => void moveTag(tag.id, -1)}
                          aria-label={t('adminTemplates:actions.moveUp')}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => void moveTag(tag.id, 1)}
                          aria-label={t('adminTemplates:actions.moveDown')}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openTagEdit(tag)}
                        >
                          {t('adminTemplates:actions.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void toggleTag(tag)}
                        >
                          {tag.enabled
                            ? t('adminTemplates:actions.disable')
                            : t('adminTemplates:actions.enable')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => void deleteTag(tag)}
                          aria-label={t('adminTemplates:actions.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog
        open={modalType === 'template'}
        onOpenChange={(open) => {
          if (!open) requestTemplateDialogClose();
        }}
      >
        <DialogContent
          ref={templateDialogContentRef}
          className="overflow-visible sm:max-w-3xl"
        >
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? t('adminTemplates:dialog.editTitle')
                : t('adminTemplates:dialog.createTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('adminTemplates:dialog.templateDescription')}
            </DialogDescription>
          </DialogHeader>
          <div
            ref={templateDialogScrollRef}
            onScroll={syncFloatingMenus}
            className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1"
          >
            <div className="rounded-2xl border bg-muted/30 p-4">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${getTemplateIconClassName(templateForm)}`}
                  style={getTemplateIconStyleAttribute(templateForm)}
                >
                  <TemplatePreviewIcon className="h-7 w-7" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">
                    {templateForm.name || t('adminTemplates:form.previewName')}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {templatePreviewIconOption.label} ·{' '}
                    {selectedTemplateAppearance}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => setVisualDialogOpen(true)}
                >
                  <Settings2 className="h-4 w-4" />
                  {t('adminTemplates:actions.visualSettings')}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('adminTemplates:form.name')}</Label>
              <Input
                value={templateForm.name}
                onChange={(e) =>
                  setTemplateForm((current) => ({
                    ...current,
                    name: e.target.value,
                  }))
                }
                placeholder={t('adminTemplates:form.namePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('adminTemplates:form.description')}</Label>
              <textarea
                value={templateForm.description}
                onChange={(e) =>
                  setTemplateForm((current) => ({
                    ...current,
                    description: e.target.value,
                  }))
                }
                className="min-h-24 w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder={t('adminTemplates:form.descriptionPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {t('adminTemplates:form.detailContent', {
                  defaultValue: 'Detail content',
                })}
              </Label>
              <textarea
                value={templateForm.detailContent}
                onChange={(e) =>
                  setTemplateForm((current) => ({
                    ...current,
                    detailContent: e.target.value,
                  }))
                }
                className="min-h-40 w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder={t('adminTemplates:form.detailContentPlaceholder', {
                  defaultValue:
                    'Longer marketplace detail content shown in the template preview dialog.',
                })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('adminTemplates:form.screenshots')}</Label>
              <div className="rounded-2xl border bg-muted/20 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {t('adminTemplates:form.screenshotsSummary', {
                        count: templateScreenshotUrls.length,
                      })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('adminTemplates:form.screenshotUrlsHint')}
                    </p>
                    <p className="font-mono text-[11px] text-muted-foreground">
                      {templateAssetFolder}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => setScreenshotDialogOpen(true)}
                  >
                    <ImagePlus className="h-4 w-4" />
                    {t('adminTemplates:actions.manageScreenshots')}
                  </Button>
                </div>

                {templateForm.coverImageUrl ? (
                  <div className="mt-4 overflow-hidden rounded-xl border bg-background">
                    <img
                      src={templateForm.coverImageUrl}
                      alt="template-cover-preview"
                      className="aspect-[16/9] w-full object-cover"
                    />
                    <div className="flex items-center justify-between gap-2 border-t px-3 py-2 text-xs text-muted-foreground">
                      <span>{t('adminTemplates:actions.coverSelected')}</span>
                      <span>
                        {templateScreenshotUrls.length} /{' '}
                        {t('adminTemplates:form.screenshotUrls')}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 rounded-xl border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                    {t('adminTemplates:form.screenshotUrlsEmpty')}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('adminTemplates:form.repo')}</Label>
                {selectedTemplateRepo ? (
                  <div className="flex items-center justify-between rounded-xl border bg-muted/30 px-3 py-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {selectedTemplateRepo.fullName}
                      </div>
                      {selectedTemplateRepo.description ? (
                        <div className="truncate text-xs text-muted-foreground">
                          {selectedTemplateRepo.description}
                        </div>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTemplateForm((current) => ({
                          ...current,
                          forgejoRepoFullName: '',
                        }));
                        setTemplateRepoMenuOpen(true);
                      }}
                    >
                      {t('adminTemplates:actions.changeRepo')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div
                      ref={templateRepoAnchorRef}
                      className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2"
                    >
                      <Input
                        value={templateRepoQuery}
                        onChange={(e) => {
                          setTemplateRepoQuery(e.target.value);
                          setTemplateRepoMenuOpen(true);
                          syncFloatingMenus();
                        }}
                        onFocus={() => {
                          setTemplateRepoMenuOpen(true);
                          syncFloatingMenus();
                        }}
                        placeholder={t('adminTemplates:form.repoPlaceholder')}
                        className="border-0 px-0 shadow-none focus-visible:ring-0"
                      />
                      <button
                        type="button"
                        className="inline-flex h-6 w-6 items-center justify-center text-muted-foreground"
                        onClick={() => {
                          setTemplateRepoMenuOpen((current) => !current);
                          syncFloatingMenus();
                        }}
                        aria-label={t('adminTemplates:actions.browseRepos')}
                      >
                        <ChevronsUpDown className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>{t('adminTemplates:form.sortOrder')}</Label>
                <Input
                  type="number"
                  value={templateForm.sortOrder}
                  onChange={(e) =>
                    setTemplateForm((current) => ({
                      ...current,
                      sortOrder: Number(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>
            {renderTemplateTagPicker()}
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={templateForm.enabled}
                  onChange={(e) =>
                    setTemplateForm((current) => ({
                      ...current,
                      enabled: e.target.checked,
                    }))
                  }
                />
                {t('adminTemplates:form.enabled')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={templateForm.featured}
                  onChange={(e) =>
                    setTemplateForm((current) => ({
                      ...current,
                      featured: e.target.checked,
                    }))
                  }
                />
                {t('adminTemplates:form.featured')}
              </label>
            </div>
          </div>
          {templateRepoMenuOpen && templateRepoMenuRect ? (
            <div
              data-template-floating-menu
              className="absolute z-[80] max-h-64 overflow-y-auto rounded-xl border bg-popover p-2 shadow-xl"
              style={{
                left: templateRepoMenuRect.left,
                top: templateRepoMenuRect.top,
                width: templateRepoMenuRect.width,
              }}
            >
              {loadingTemplateRepos ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {t('adminTemplates:actions.loadingRepos')}
                </div>
              ) : filteredTemplateRepos.length ? (
                <div className="space-y-1">
                  {filteredTemplateRepos.map((repo) => (
                    <button
                      key={repo.id}
                      type="button"
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition hover:bg-muted"
                      onClick={() => {
                        const suggestedName = repo.name
                          .replace(/-/g, ' ')
                          .replace(/\b\w/g, (m) => m.toUpperCase());
                        setTemplateForm((current) => ({
                          ...current,
                          forgejoRepoFullName: repo.fullName,
                          name: current.name || suggestedName,
                        }));
                        setTemplateRepoQuery('');
                        setTemplateRepoMenuOpen(false);
                      }}
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">
                          {repo.fullName}
                        </div>
                        {repo.description ? (
                          <div className="truncate text-xs text-muted-foreground">
                            {repo.description}
                          </div>
                        ) : null}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {t('adminTemplates:form.repoEmpty')}
                </div>
              )}
            </div>
          ) : null}
          {templateTagMenuOpen && templateTagMenuRect ? (
            <div
              data-template-floating-menu
              className="absolute z-[80] max-h-72 overflow-y-auto rounded-xl border bg-popover p-2 shadow-xl"
              style={{
                left: templateTagMenuRect.left,
                top: templateTagMenuRect.top,
                width: templateTagMenuRect.width,
              }}
            >
              {filteredTemplateTagGroups.length ? (
                <div className="space-y-3">
                  {filteredTemplateTagGroups.map(({ group, tags }) => (
                    <div key={group.id} className="space-y-2">
                      <div className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {group.name}
                      </div>
                      <div className="space-y-1">
                        {tags.map((tag) => {
                          const selected = templateForm.tagIds.includes(tag.id);
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition hover:bg-muted"
                              onClick={() =>
                                selected
                                  ? removeTemplateTag(tag.id)
                                  : addTemplateTag(tag.id)
                              }
                            >
                              <div className="min-w-0">
                                <div className="text-sm font-medium">
                                  {tag.name}
                                </div>
                                {tag.description ? (
                                  <div className="truncate text-xs text-muted-foreground">
                                    {tag.description}
                                  </div>
                                ) : null}
                              </div>
                              <div className="ml-3 flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className={getCompactTemplateBadgeClassName(
                                    getTemplateTagColorClassName(
                                      tag.colorToken,
                                    ),
                                  )}
                                >
                                  {group.name}
                                </Badge>
                                {selected ? (
                                  <Check className="h-4 w-4 text-primary" />
                                ) : null}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {t('adminTemplates:form.templateTagsEmpty')}
                </div>
              )}
            </div>
          ) : null}
          <Dialog open={visualDialogOpen} onOpenChange={setVisualDialogOpen}>
            <DialogContent className="sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {t('adminTemplates:visualDialog.title')}
                </DialogTitle>
                <DialogDescription>
                  {t('adminTemplates:visualDialog.description')}
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-2xl border bg-muted/30 p-4">
                <div className="flex items-center gap-4">
                  <div
                    className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl ${getTemplateIconClassName(templateForm)}`}
                    style={getTemplateIconStyleAttribute(templateForm)}
                  >
                    <TemplatePreviewIcon className="h-8 w-8" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-base font-semibold">
                      {templateForm.name ||
                        t('adminTemplates:form.previewName')}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {templatePreviewIconOption.label}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {selectedTemplateAppearance}
                    </div>
                  </div>
                </div>
              </div>
              <Tabs defaultValue="icon" className="space-y-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="icon">
                    {t('adminTemplates:visualDialog.tabs.icon')}
                  </TabsTrigger>
                  <TabsTrigger value="color">
                    {t('adminTemplates:visualDialog.tabs.color')}
                  </TabsTrigger>
                  <TabsTrigger value="gradient">
                    {t('adminTemplates:visualDialog.tabs.gradient')}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="icon" className="space-y-3">
                  <Input
                    value={visualIconQuery}
                    onChange={(event) => setVisualIconQuery(event.target.value)}
                    placeholder={t('adminTemplates:visualDialog.searchIcons')}
                  />
                  <div
                    ref={visualIconScrollRef}
                    onScroll={handleTemplateIconScroll}
                    className="max-h-[42vh] overflow-y-auto pr-1"
                  >
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {visibleTemplateIconOptions.map((option) => {
                        const Icon = option.icon;
                        const selected = templateForm.icon === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            className={`flex items-center gap-3 rounded-xl border p-3 text-left transition hover:bg-muted ${selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''}`}
                            onClick={() =>
                              setTemplateForm((current) => ({
                                ...current,
                                icon: option.value,
                              }))
                            }
                          >
                            <span
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${getTemplateIconClassName({ ...templateForm, icon: option.value })}`}
                              style={getTemplateIconStyleAttribute({
                                ...templateForm,
                                icon: option.value,
                              })}
                            >
                              <Icon className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 text-sm font-medium">
                              {option.label}
                            </span>
                            {selected ? (
                              <Check className="ml-auto h-4 w-4 text-primary" />
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between py-3 text-xs text-muted-foreground">
                      <span>
                        {t('adminTemplates:visualDialog.iconPagination', {
                          shown: visibleTemplateIconOptions.length,
                          total: filteredTemplateIconOptions.length,
                        })}
                      </span>
                      {hasMoreTemplateIcons ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={loadMoreTemplateIcons}
                        >
                          {t('adminTemplates:visualDialog.loadMoreIcons')}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="color" className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('adminTemplates:visualDialog.colorLabel')}</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="color"
                        value={plainIconColor}
                        onChange={(event) =>
                          setTemplateForm((current) => ({
                            ...current,
                            iconStyle: 'plain',
                            iconColor: event.target.value,
                          }))
                        }
                        className="h-10 w-16 p-1"
                      />
                      <Input
                        value={plainIconColor}
                        onChange={(event) =>
                          setTemplateForm((current) => ({
                            ...current,
                            iconStyle: 'plain',
                            iconColor: event.target.value,
                          }))
                        }
                        placeholder="#0f766e"
                      />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="gradient" className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>
                        {t('adminTemplates:visualDialog.gradientStart')}
                      </Label>
                      <Input
                        type="color"
                        value={gradientStart}
                        onChange={(event) =>
                          setTemplateForm((current) => ({
                            ...current,
                            iconStyle: 'gradient',
                            iconColor: createGradient(
                              event.target.value,
                              gradientEnd,
                            ),
                          }))
                        }
                        className="h-10 w-full p-1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        {t('adminTemplates:visualDialog.gradientEnd')}
                      </Label>
                      <Input
                        type="color"
                        value={gradientEnd}
                        onChange={(event) =>
                          setTemplateForm((current) => ({
                            ...current,
                            iconStyle: 'gradient',
                            iconColor: createGradient(
                              gradientStart,
                              event.target.value,
                            ),
                          }))
                        }
                        className="h-10 w-full p-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>
                      {t('adminTemplates:visualDialog.gradientCss')}
                    </Label>
                    <Input
                      value={
                        templateForm.iconStyle === 'gradient'
                          ? templateForm.iconColor
                          : createGradient(gradientStart, gradientEnd)
                      }
                      onChange={(event) =>
                        setTemplateForm((current) => ({
                          ...current,
                          iconStyle: 'gradient',
                          iconColor: event.target.value,
                        }))
                      }
                      placeholder="linear-gradient(135deg, #0f766e, #06b6d4)"
                    />
                  </div>
                </TabsContent>
              </Tabs>
              <DialogFooter>
                <Button
                  type="button"
                  onClick={() => setVisualDialogOpen(false)}
                >
                  {t('common:done', { defaultValue: 'Done' })}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <DialogFooter>
            <Button variant="outline" onClick={requestTemplateDialogClose}>
              {t('common:cancel')}
            </Button>
            <Button onClick={() => void saveTemplate()} disabled={saving}>
              {saving
                ? t('adminTemplates:actions.saving')
                : t('adminTemplates:actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={showTemplateCloseConfirm}
        onOpenChange={setShowTemplateCloseConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('adminTemplates:closeConfirm.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('adminTemplates:closeConfirm.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('common:cancel', { defaultValue: 'Cancel' })}
            </AlertDialogCancel>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowTemplateCloseConfirm(false);
                resetModal();
              }}
            >
              {t('adminTemplates:closeConfirm.quit')}
            </Button>
            <AlertDialogAction
              onClick={async (event) => {
                event.preventDefault();
                const saved = await saveTemplate();
                if (saved) {
                  setShowTemplateCloseConfirm(false);
                }
              }}
            >
              {saving
                ? t('adminTemplates:actions.saving')
                : t('adminTemplates:closeConfirm.saveAndQuit')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={screenshotDialogOpen}
        onOpenChange={setScreenshotDialogOpen}
      >
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {t('adminTemplates:screenshotDialog.title')}
            </DialogTitle>
            <DialogDescription>
              {t('adminTemplates:screenshotDialog.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-muted/20 p-3">
              <div className="space-y-1">
                <div className="text-sm font-medium">
                  {t('adminTemplates:screenshotDialog.folderLabel')}
                </div>
                <div className="font-mono text-xs text-muted-foreground">
                  {templateAssetFolder}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => screenshotInputRef.current?.click()}
                  disabled={uploadingScreenshots}
                >
                  {uploadingScreenshots ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {t('adminTemplates:actions.uploadScreenshots')}
                </Button>
                <input
                  ref={screenshotInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(event) =>
                    void uploadScreenshots(event.target.files)
                  }
                />
              </div>
            </div>

            {templateScreenshotUrls.length > 0 ? (
              <div className="space-y-3">
                {templateScreenshotUrls.map((url, index) => (
                  <div
                    key={`${url}-${index}`}
                    className="grid gap-3 rounded-2xl border bg-background p-3 md:grid-cols-[180px_minmax(0,1fr)_auto] md:items-center"
                  >
                    <div className="overflow-hidden rounded-xl border bg-muted/20">
                      <img
                        src={url}
                        alt={`template-screenshot-${index + 1}`}
                        className="aspect-[16/10] w-full object-cover"
                      />
                    </div>
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant={
                            templateForm.coverImageUrl === url
                              ? 'default'
                              : 'secondary'
                          }
                          className={getCompactTemplateBadgeClassName()}
                        >
                          {templateForm.coverImageUrl === url
                            ? t('adminTemplates:actions.coverSelected')
                            : t('adminTemplates:screenshotDialog.imageLabel', {
                                index: index + 1,
                              })}
                        </Badge>
                        <span className="font-mono text-xs text-muted-foreground">
                          {url.split('/').pop()}
                        </span>
                      </div>
                      <p
                        className="truncate text-xs text-muted-foreground"
                        title={url}
                      >
                        {url}
                      </p>
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={index === 0}
                        onClick={() => moveScreenshot(index, -1)}
                        aria-label={t('adminTemplates:actions.moveUp')}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={index === templateScreenshotUrls.length - 1}
                        onClick={() => moveScreenshot(index, 1)}
                        aria-label={t('adminTemplates:actions.moveDown')}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          setTemplateForm((current) => ({
                            ...current,
                            coverImageUrl: url,
                          }))
                        }
                        aria-label={t('adminTemplates:actions.setAsCover')}
                      >
                        <Star
                          className={`h-4 w-4 ${templateForm.coverImageUrl === url ? 'fill-current' : ''}`}
                        />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => removeScreenshotUrl(url)}
                        aria-label={t('adminTemplates:actions.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed px-3 py-10 text-center text-sm text-muted-foreground">
                {t('adminTemplates:form.screenshotUrlsEmpty')}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setScreenshotDialogOpen(false)}
            >
              {t('common:cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => setScreenshotDialogOpen(false)}
            >
              {t('adminTemplates:actions.doneScreenshots')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={modalType === 'group'}
        onOpenChange={(open) => !open && resetModal()}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? t('adminTemplates:dialog.editGroupTitle')
                : t('adminTemplates:dialog.createGroupTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('adminTemplates:dialog.groupDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('adminTemplates:groupForm.id')}</Label>
                <Input
                  value={groupForm.id}
                  disabled={Boolean(editingId)}
                  onChange={(e) =>
                    setGroupForm((current) => ({
                      ...current,
                      id: e.target.value,
                    }))
                  }
                  placeholder={t('adminTemplates:groupForm.idPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('adminTemplates:groupForm.name')}</Label>
                <Input
                  value={groupForm.name}
                  onChange={(e) =>
                    setGroupForm((current) => ({
                      ...current,
                      name: e.target.value,
                    }))
                  }
                  placeholder={t('adminTemplates:groupForm.namePlaceholder')}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('adminTemplates:groupForm.description')}</Label>
              <textarea
                value={groupForm.description}
                onChange={(e) =>
                  setGroupForm((current) => ({
                    ...current,
                    description: e.target.value,
                  }))
                }
                className="min-h-24 w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder={t(
                  'adminTemplates:groupForm.descriptionPlaceholder',
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('adminTemplates:groupForm.sortOrder')}</Label>
                <Input
                  type="number"
                  value={groupForm.sortOrder}
                  onChange={(e) =>
                    setGroupForm((current) => ({
                      ...current,
                      sortOrder: Number(e.target.value) || 0,
                    }))
                  }
                />
              </div>
              <label className="flex items-center gap-2 text-sm pt-8">
                <input
                  type="checkbox"
                  checked={groupForm.enabled}
                  onChange={(e) =>
                    setGroupForm((current) => ({
                      ...current,
                      enabled: e.target.checked,
                    }))
                  }
                />
                {t('adminTemplates:groupForm.enabled')}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetModal}>
              {t('common:cancel')}
            </Button>
            <Button onClick={() => void saveGroup()} disabled={saving}>
              {saving
                ? t('adminTemplates:actions.saving')
                : t('adminTemplates:actions.saveGroup')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={modalType === 'tag'}
        onOpenChange={(open) => !open && resetModal()}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? t('adminTemplates:dialog.editTagTitle')
                : t('adminTemplates:dialog.createTagTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('adminTemplates:dialog.tagDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('adminTemplates:tagForm.id')}</Label>
                <Input
                  value={tagForm.id}
                  disabled={Boolean(editingId)}
                  onChange={(e) =>
                    setTagForm((current) => ({
                      ...current,
                      id: e.target.value,
                    }))
                  }
                  placeholder={t('adminTemplates:tagForm.idPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('adminTemplates:tagForm.name')}</Label>
                <Input
                  value={tagForm.name}
                  onChange={(e) =>
                    setTagForm((current) => ({
                      ...current,
                      name: e.target.value,
                    }))
                  }
                  placeholder={t('adminTemplates:tagForm.namePlaceholder')}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('adminTemplates:tagForm.description')}</Label>
              <textarea
                value={tagForm.description}
                onChange={(e) =>
                  setTagForm((current) => ({
                    ...current,
                    description: e.target.value,
                  }))
                }
                className="min-h-24 w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder={t('adminTemplates:tagForm.descriptionPlaceholder')}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('adminTemplates:tagForm.group')}</Label>
                <Select
                  value={tagForm.groupId}
                  onValueChange={(value) =>
                    setTagForm((current) => ({ ...current, groupId: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t('adminTemplates:tagForm.groupPlaceholder')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {orderedGroups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('adminTemplates:tagForm.colorToken')}</Label>
                <Select
                  value={tagForm.colorToken}
                  onValueChange={(value) =>
                    setTagForm((current) => ({
                      ...current,
                      colorToken: value as TemplateTagColorToken,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map((color) => (
                      <SelectItem key={color} value={color}>
                        {color}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('adminTemplates:tagForm.sortOrder')}</Label>
                <Input
                  type="number"
                  value={tagForm.sortOrder}
                  onChange={(e) =>
                    setTagForm((current) => ({
                      ...current,
                      sortOrder: Number(e.target.value) || 0,
                    }))
                  }
                />
              </div>
              <label className="flex items-center gap-2 text-sm pt-8">
                <input
                  type="checkbox"
                  checked={tagForm.enabled}
                  onChange={(e) =>
                    setTagForm((current) => ({
                      ...current,
                      enabled: e.target.checked,
                    }))
                  }
                />
                {t('adminTemplates:tagForm.enabled')}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetModal}>
              {t('common:cancel')}
            </Button>
            <Button onClick={() => void saveTag()} disabled={saving}>
              {saving
                ? t('adminTemplates:actions.saving')
                : t('adminTemplates:actions.saveTag')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
